// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ImageSourcePropType } from 'react-native';
import { Platform } from 'react-native';

import { buildMediaUrl } from '@/services/mediaUrl';

export type AssetImageTarget = 'web' | 'phone' | 'auto';

export type AssetImageRequestOptions = {
  width?: number;
  height?: number;
  target?: AssetImageTarget;
  bucketStep?: number;
};

type ImageSizeProfile = {
  web: { width: number; height: number };
  phone: { width: number; height: number };
};

const PRESET_SIZE_PROFILES: Record<string, ImageSizeProfile> = {
  'game-select': {
    web: { width: 336, height: 336 },
    phone: { width: 132, height: 132 },
  },
  icon: {
    web: { width: 64, height: 64 },
    phone: { width: 48, height: 48 },
  },
  hero: {
    web: { width: 720, height: 720 },
    phone: { width: 640, height: 640 },
  },
};

export function getAssetImageUri(path: string, preset = 'default', pagePath?: string, options?: AssetImageRequestOptions): string {
  const normalizedPath = normalizeAssetImagePath(path);
  const dimensions = resolveRequestedDimensions(preset, options);
  return buildMediaUrl('images', normalizedPath, preset, {
    ...(pagePath ? { pagePath } : {}),
    ...dimensions,
    bucketStep: options?.bucketStep,
  });
}

export function getAssetImageSource(path: string, preset = 'default', pagePath?: string, options?: AssetImageRequestOptions): ImageSourcePropType {
  return { uri: getAssetImageUri(path, preset, pagePath, options) };
}

function normalizeAssetImagePath(path: string): string {
  const cleaned = (path ?? '').trim().replace(/\\/g, '/').replace(/^\/+/, '');
  if (!cleaned) {
    return 'images/unknown.png';
  }

  if (cleaned.startsWith('assets-generated/images/') || cleaned.startsWith('images/')) {
    return cleaned;
  }

  if (cleaned.startsWith('assets/images/')) {
    return `images/${cleaned.slice('assets/images/'.length)}`;
  }

  if (cleaned.startsWith('assets/')) {
    return `images/${cleaned.slice('assets/'.length)}`;
  }

  return cleaned.includes('/') ? cleaned : `images/${cleaned}`;
}

function resolveRequestedDimensions(
  preset: string,
  options?: AssetImageRequestOptions,
): { width?: number; height?: number } {
  if (Number.isFinite(options?.width) || Number.isFinite(options?.height)) {
    return {
      width: options?.width,
      height: options?.height,
    };
  }

  const profile = PRESET_SIZE_PROFILES[(preset ?? '').trim().toLowerCase()];
  if (!profile) {
    return {};
  }

  const target = resolveTarget(options?.target);
  const size = target === 'web' ? profile.web : profile.phone;
  return {
    width: size.width,
    height: size.height,
  };
}

function resolveTarget(target?: AssetImageTarget): Exclude<AssetImageTarget, 'auto'> {
  if (target === 'web' || target === 'phone') {
    return target;
  }

  return Platform.OS === 'web' ? 'web' : 'phone';
}
